import axios from "axios";
import { getCookie } from "../../utils/cookies";
import {
  ANALYTICS_LOADING,
  ANALYTICS_ERROR,
  CLEAR_ANALYTICS,
  FETCH_ANALYTICS_OVERVIEW,
  FETCH_ANALYTICS_VIDEO_STATS,
  FETCH_ANALYTICS_TOP_VIDEOS,
  FETCH_ANALYTICS_VIDEO_TIMELINE,
  FETCH_ANALYTICS_COMMENT_STATS,
  FETCH_ANALYTICS_TOP_COMMENTERS,
  FETCH_ANALYTICS_REACTION_STATS,
  FETCH_ANALYTICS_TOP_REACTED,
  FETCH_ANALYTICS_POST_STATS,
  FETCH_ANALYTICS_BADGE_STATS,
} from "./types";

const API_URL = process.env.REACT_APP_GOLANG_API;

const setAuthHeader = () => {
  const token = getCookie("_session");
  if (token) {
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${JSON.parse(token)}`;
    } catch {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }
};

const buildParams = (startDate, endDate, extra = {}) => {
  const params = new URLSearchParams();
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  Object.entries(extra).forEach(([k, v]) => {
    if (v !== undefined && v !== null) params.append(k, v);
  });
  return params.toString();
};

export const fetchOverview = (startDate, endDate) => async (dispatch) => {
  try {
    dispatch({ type: ANALYTICS_LOADING, payload: { overview: true } });
    setAuthHeader();
    const qs = buildParams(startDate, endDate);
    const response = await axios.get(`${API_URL}/api/v2/analytics/overview?${qs}`);
    dispatch({ type: FETCH_ANALYTICS_OVERVIEW, payload: response.data });
  } catch (error) {
    dispatch({ type: ANALYTICS_ERROR, payload: error.response?.data?.message || error.message });
  }
};

export const fetchVideoStats = (startDate, endDate) => async (dispatch) => {
  try {
    dispatch({ type: ANALYTICS_LOADING, payload: { videos: true } });
    setAuthHeader();
    const qs = buildParams(startDate, endDate);
    const response = await axios.get(`${API_URL}/api/v2/analytics/videos?${qs}`);
    dispatch({ type: FETCH_ANALYTICS_VIDEO_STATS, payload: response.data });
  } catch (error) {
    dispatch({ type: ANALYTICS_ERROR, payload: error.response?.data?.message || error.message });
  }
};

export const fetchTopVideos = (startDate, endDate, limit = 10) => async (dispatch) => {
  try {
    dispatch({ type: ANALYTICS_LOADING, payload: { topVideos: true } });
    setAuthHeader();
    const qs = buildParams(startDate, endDate, { limit });
    const response = await axios.get(`${API_URL}/api/v2/analytics/videos/top?${qs}`);
    dispatch({ type: FETCH_ANALYTICS_TOP_VIDEOS, payload: response.data.videos });
  } catch (error) {
    dispatch({ type: ANALYTICS_ERROR, payload: error.response?.data?.message || error.message });
  }
};

export const fetchVideoTimeline = (startDate, endDate, groupBy = "day") => async (dispatch) => {
  try {
    dispatch({ type: ANALYTICS_LOADING, payload: { timeline: true } });
    setAuthHeader();
    const qs = buildParams(startDate, endDate, { groupBy });
    const response = await axios.get(`${API_URL}/api/v2/analytics/videos/timeline?${qs}`);
    dispatch({ type: FETCH_ANALYTICS_VIDEO_TIMELINE, payload: response.data });
  } catch (error) {
    dispatch({ type: ANALYTICS_ERROR, payload: error.response?.data?.message || error.message });
  }
};

export const fetchCommentStats = (startDate, endDate) => async (dispatch) => {
  try {
    dispatch({ type: ANALYTICS_LOADING, payload: { comments: true } });
    setAuthHeader();
    const qs = buildParams(startDate, endDate);
    const response = await axios.get(`${API_URL}/api/v2/analytics/comments?${qs}`);
    dispatch({ type: FETCH_ANALYTICS_COMMENT_STATS, payload: response.data });
  } catch (error) {
    dispatch({ type: ANALYTICS_ERROR, payload: error.response?.data?.message || error.message });
  }
};

export const fetchTopCommenters = (startDate, endDate, limit = 10) => async (dispatch) => {
  try {
    dispatch({ type: ANALYTICS_LOADING, payload: { topCommenters: true } });
    setAuthHeader();
    const qs = buildParams(startDate, endDate, { limit });
    const response = await axios.get(`${API_URL}/api/v2/analytics/comments/top-users?${qs}`);
    dispatch({ type: FETCH_ANALYTICS_TOP_COMMENTERS, payload: response.data.users });
  } catch (error) {
    dispatch({ type: ANALYTICS_ERROR, payload: error.response?.data?.message || error.message });
  }
};

export const fetchReactionStats = (startDate, endDate) => async (dispatch) => {
  try {
    dispatch({ type: ANALYTICS_LOADING, payload: { reactions: true } });
    setAuthHeader();
    const qs = buildParams(startDate, endDate);
    const response = await axios.get(`${API_URL}/api/v2/analytics/reactions?${qs}`);
    dispatch({ type: FETCH_ANALYTICS_REACTION_STATS, payload: response.data });
  } catch (error) {
    dispatch({ type: ANALYTICS_ERROR, payload: error.response?.data?.message || error.message });
  }
};

export const fetchTopReactedVideos = (startDate, endDate, limit = 10) => async (dispatch) => {
  try {
    dispatch({ type: ANALYTICS_LOADING, payload: { topReacted: true } });
    setAuthHeader();
    const qs = buildParams(startDate, endDate, { limit });
    const response = await axios.get(`${API_URL}/api/v2/analytics/reactions/top-videos?${qs}`);
    dispatch({ type: FETCH_ANALYTICS_TOP_REACTED, payload: response.data.videos });
  } catch (error) {
    dispatch({ type: ANALYTICS_ERROR, payload: error.response?.data?.message || error.message });
  }
};

export const fetchPostStats = (startDate, endDate) => async (dispatch) => {
  try {
    dispatch({ type: ANALYTICS_LOADING, payload: { posts: true } });
    setAuthHeader();
    const qs = buildParams(startDate, endDate);
    const response = await axios.get(`${API_URL}/api/v2/analytics/posts?${qs}`);
    dispatch({ type: FETCH_ANALYTICS_POST_STATS, payload: response.data });
  } catch (error) {
    dispatch({ type: ANALYTICS_ERROR, payload: error.response?.data?.message || error.message });
  }
};

export const fetchBadgeStats = (startDate, endDate) => async (dispatch) => {
  try {
    dispatch({ type: ANALYTICS_LOADING, payload: { badges: true } });
    setAuthHeader();
    const qs = buildParams(startDate, endDate);
    const response = await axios.get(`${API_URL}/api/v2/analytics/badges?${qs}`);
    dispatch({ type: FETCH_ANALYTICS_BADGE_STATS, payload: response.data });
  } catch (error) {
    dispatch({ type: ANALYTICS_ERROR, payload: error.response?.data?.message || error.message });
  }
};

export const clearAnalytics = () => ({ type: CLEAR_ANALYTICS });
